import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { parsePhoneNumber } from 'react-phone-number-input'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import {
  PhoneNumberCountry,
  CountryAndPhone,
} from '@/components/phone-number-country'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import countries from '@/core/constants/countries.json'
import {
  createVendorUser,
  deleteVendorUser,
  IVendorUser,
  updateVendorUser,
  VendorUserPermission,
} from '@/core/services/vendors'
import { DeleteConfirmationModal } from '@/home/components/DeleteConfirmationModal'

interface CreateUserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: string
  onSuccess: () => void
  onError: (message: string) => void
  user?: IVendorUser | null
}

interface IUserFormValues {
  name: string
  phone: CountryAndPhone
  permissions: VendorUserPermission[]
}

const PERMISSION_OPTIONS: { value: VendorUserPermission; label: string }[] = [
  { value: 'LOCATIONS', label: 'Locations' },
  { value: 'USERS', label: 'Users' },
  { value: 'TAGS', label: 'Tags' },
  { value: 'PRODUCTS', label: 'Products' },
  { value: 'SALES', label: 'Sales' },
]

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  phone: yup.object().shape({
    countryCode: yup.string().required('Country is required'),
    countryCodeValue: yup.string(),
    phoneNumber: yup.string().required('Phone number is required'),
  }),
  permissions: yup.array().of(yup.string().required()).default([]),
})

export const CreateUserModal = ({
  open,
  onOpenChange,
  vendorId,
  onSuccess,
  onError,
  user,
}: CreateUserModalProps) => {
  const isEditMode = !!user
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<IUserFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      phone: { countryCode: '', countryCodeValue: '', phoneNumber: '' },
      permissions: [],
    },
  })

  useEffect(() => {
    if (!open) return
    if (user) {
      setValue('name', user.name, { shouldValidate: true })
      setValue('permissions', user.permissions || [], { shouldValidate: true })

      let phone: CountryAndPhone = {
        countryCode: '',
        countryCodeValue: '',
        phoneNumber: user.phone,
      }
      try {
        const parsed = parsePhoneNumber(user.phone)
        if (parsed) {
          const country = countries.find((c) => c.code === parsed.country)
          phone = {
            countryCode: parsed.country || '',
            countryCodeValue: country?.phonePrefix || '',
            phoneNumber: parsed.nationalNumber,
          }
        }
      } catch {
        // fallback to raw phone string
      }
      setValue('phone', phone, { shouldValidate: true })
    }
    return () => {
      reset({
        name: '',
        phone: { countryCode: '', countryCodeValue: '', phoneNumber: '' },
        permissions: [],
      })
    }
  }, [open, user])

  const handleClose = () => {
    onOpenChange(false)
  }

  const submit = async (values: IUserFormValues) => {
    try {
      const phone = `${values.phone.countryCodeValue}${values.phone.phoneNumber}`
      if (isEditMode) {
        await updateVendorUser(vendorId, user.id, {
          name: values.name,
          phone,
          permissions: values.permissions,
        })
      } else {
        await createVendorUser(vendorId, {
          id: vendorId,
          name: values.name,
          phone,
          permissions: values.permissions,
        })
      }
      onSuccess()
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
    handleClose()
  }

  const handleDeleteUser = async () => {
    if (!user) return
    try {
      await deleteVendorUser(vendorId, user.id)
      onSuccess()
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="Vendor Entity"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">User</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode ? (
              <Button
                onClick={() => setDeleteModalOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete User
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={isEditMode && Object.keys(dirtyFields).length === 0}
                form="create-user-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-user-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-6"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneNumberCountry
                  buttonClassName="border border-monochrome-120 !rounded-[16px] h-[36px] items-center px-4 pb-0 text-monochrome-2 bg-monochrome-3-60"
                  textInputProps={{
                    outline: true,
                    className: '!bg-monochrome-3-60 !rounded-[16px]',
                    withClear: false,
                    prefixClassName: 'left-2 text-monochrome-1',
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={
                    (errors?.phone as any)?.countryCode?.message ||
                    (errors?.phone as any)?.phoneNumber?.message
                  }
                />
              )}
            />
          </div>

          <div className="border-t border-monochrome-120 pt-6">
            <div className="text-2xl font-bold leading-none text-monochrome-1 mb-4">
              Permissions
            </div>

            <div className="flex flex-col gap-3">
              <Controller
                name="permissions"
                control={control}
                render={({ field }) => (
                  <>
                    {PERMISSION_OPTIONS.map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <Checkbox
                          checked={field.value.includes(value)}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...field.value, value]
                              : field.value.filter((v) => v !== value)
                            field.onChange(next)
                          }}
                        />
                        <span className="text-body-medium text-monochrome-1">
                          {label}
                        </span>
                      </label>
                    ))}
                  </>
                )}
              />
            </div>
          </div>
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModalOpen}
        setDeleteConfirmationModal={setDeleteModalOpen}
        deleteFunc={handleDeleteUser}
        deleteTitle="User"
        deletedItem={user?.name}
      />
    </>
  )
}
